/**
 * PLZ → Bundesland.
 *
 * Web und App: Veranstalter-Formulare, und die Heimatregion eines Kontos aus seiner Postleitzahl
 * (`regionFromLocation` in `accountDetails.ts`).
 *
 * Steht seit dem 26.08.2026 hier und nicht mehr im Veranstalter-Dialog: seit die Stammdaten
 * **auch** direkt im Briefkopf der Veranstalterseite bearbeitet werden, brauchen zwei Formulare
 * dieselbe Erkennung. Zwei Tabellen wären zwei Wahrheiten.
 *
 * Nur deutsche PLZ. Der Aufrufer muss vorher prüfen, ob überhaupt Deutschland gemeint ist
 * (`countryOfBundesland`) — sonst stempelt „20121" (Mailand) still „Bayern" über „Italien".
 *
 * Bis zum 07.09.2026 entschied die zweistellige Vorwahl. Das ist zu grob: die Leitregionen
 * folgen der Post, nicht den Landesgrenzen. „21" liegt zur Hälfte in Niedersachsen (Lüneburg
 * 21335, Stade 21682), „22" reicht nach Schleswig-Holstein (Norderstedt 22844), „33" nach
 * Niedersachsen, „63" nach Bayern — 869 von 10.812 PLZ bekamen das falsche Land (Trello-Karte
 * „Autovorschlag Bundesland nach PLZ stimmt nicht"). Die Tabelle unten sind die Wechselstellen
 * aus dem GeoNames-PLZ-Verzeichnis (CC BY 4.0, Stand 07.09.2026): ab PLZ X gilt Land Y bis zur
 * nächsten Zeile. Eine PLZ, die dort nicht steht, fällt in den Bereich davor — PLZ-Blöcke sind
 * räumlich zusammenhängend, deshalb ist das der richtige Rückfall. Großkunden-PLZ (Firmen-
 * adressen, „10875 Daimler") sind vorher herausgefiltert, sie lägen sonst als Inseln im falschen Land. Bei den wenigen PLZ, die
 * über eine Landesgrenze reichen (z. B. 21039 Hamburg/Schleswig-Holstein), zählt die Mehrheit
 * der Orte; die Erkennung ist ein Vorschlag, den das Formular von Hand überschreiben lässt.
 *
 * Neu erzeugen: GeoNames `DE.zip` (download.geonames.org/export/zip/), je PLZ das häufigste
 * `admin name1` (englische Namen auf die deutschen abbilden, Umlaute nach ASCII, Zeilen mit
 * Firmennamen wie „GmbH"/„AG" weglassen), sortiert durchgehen und nur die Zeilen behalten, an
 * denen das Land wechselt; Ein-PLZ-Inseln zwischen zwei gleichen Ländern dem Umfeld zuschlagen.
 */
const PLZ_BREAKPOINTS: [number, string][] = [
  [1054, 'Sachsen'], [1945, 'Brandenburg'], [2619, 'Sachsen'],
  [3039, 'Brandenburg'], [4022, 'Sachsen'], [4600, 'Thueringen'],
  [4643, 'Sachsen'], [4895, 'Brandenburg'], [6073, 'Sachsen-Anhalt'],
  [6553, 'Thueringen'], [6618, 'Sachsen-Anhalt'], [7318, 'Thueringen'],
  [8037, 'Sachsen'], [10086, 'Berlin'], [14322, 'Brandenburg'],
  [17030, 'Mecklenburg-Vorpommern'], [17268, 'Brandenburg'], [17309, 'Mecklenburg-Vorpommern'],
  [19309, 'Brandenburg'], [19370, 'Mecklenburg-Vorpommern'], [20038, 'Hamburg'],
  [21217, 'Niedersachsen'], [21462, 'Schleswig-Holstein'], [21614, 'Niedersachsen'],
  [22032, 'Hamburg'], [22841, 'Schleswig-Holstein'], [23923, 'Mecklenburg-Vorpommern'],
  [24088, 'Schleswig-Holstein'], [26101, 'Niedersachsen'], [27498, 'Schleswig-Holstein'],
  [27499, 'Hamburg'], [27554, 'Bremen'], [27607, 'Niedersachsen'],
  [28100, 'Bremen'], [28788, 'Niedersachsen'], [29410, 'Sachsen-Anhalt'],
  [29439, 'Niedersachsen'], [32042, 'Nordrhein-Westfalen'], [34105, 'Hessen'],
  [34346, 'Niedersachsen'], [34359, 'Hessen'], [34414, 'Nordrhein-Westfalen'],
  [34454, 'Hessen'], [36404, 'Thueringen'], [37069, 'Niedersachsen'],
  [37213, 'Hessen'], [37308, 'Thueringen'], [37412, 'Niedersachsen'],
  [37669, 'Nordrhein-Westfalen'], [37697, 'Niedersachsen'], [38486, 'Sachsen-Anhalt'],
  [38516, 'Niedersachsen'], [38817, 'Sachsen-Anhalt'], [40180, 'Nordrhein-Westfalen'],
  [48455, 'Niedersachsen'], [48493, 'Nordrhein-Westfalen'], [48499, 'Niedersachsen'],
  [48563, 'Nordrhein-Westfalen'], [49068, 'Niedersachsen'], [49475, 'Nordrhein-Westfalen'],
  [49562, 'Niedersachsen'], [50123, 'Nordrhein-Westfalen'], [53423, 'Rheinland-Pfalz'],
  [53602, 'Nordrhein-Westfalen'], [54187, 'Rheinland-Pfalz'], [55246, 'Hessen'],
  [55257, 'Rheinland-Pfalz'], [57068, 'Nordrhein-Westfalen'], [57517, 'Rheinland-Pfalz'],
  [58079, 'Nordrhein-Westfalen'], [59969, 'Hessen'], [63715, 'Bayern'],
  [64271, 'Hessen'], [65558, 'Rheinland-Pfalz'], [65589, 'Hessen'],
  [65623, 'Rheinland-Pfalz'], [65717, 'Hessen'], [66088, 'Saarland'],
  [66482, 'Rheinland-Pfalz'], [66538, 'Saarland'], [66849, 'Rheinland-Pfalz'],
  [68122, 'Baden-Wuerttemberg'], [68517, 'Hessen'], [68526, 'Baden-Wuerttemberg'],
  [68619, 'Hessen'], [68721, 'Baden-Wuerttemberg'], [69483, 'Hessen'],
  [69493, 'Baden-Wuerttemberg'], [69517, 'Hessen'], [70131, 'Baden-Wuerttemberg'],
  [76725, 'Rheinland-Pfalz'], [77642, 'Baden-Wuerttemberg'], [80097, 'Bayern'],
  [88041, 'Baden-Wuerttemberg'], [88129, 'Bayern'], [88209, 'Baden-Wuerttemberg'],
  [89229, 'Bayern'], [89516, 'Baden-Wuerttemberg'], [90290, 'Bayern'],
  [96515, 'Thueringen'], [97024, 'Bayern'], [97896, 'Baden-Wuerttemberg'],
  [97901, 'Bayern'], [97922, 'Baden-Wuerttemberg'], [98496, 'Thueringen'],
];

export function getPlzBundesland(plz: string): string | null {
  const digits = plz.trim();
  if (!/^\d{5}$/.test(digits)) return null;
  const n = Number(digits);
  if (n < PLZ_BREAKPOINTS[0][0]) return null;
  // Letzte Wechselstelle, die nicht hinter der PLZ liegt.
  let lo = 0;
  let hi = PLZ_BREAKPOINTS.length - 1;
  while (lo < hi) {
    const mid = Math.ceil((lo + hi) / 2);
    if (PLZ_BREAKPOINTS[mid][0] <= n) lo = mid; else hi = mid - 1;
  }
  return PLZ_BREAKPOINTS[lo][1];
}
